import { useCallback, useMemo, useRef, useState } from "react";
import {
    alsFehler,
    alsFertig,
    alsLaufend,
    entfernen,
    fertigeRaeumen,
    hinzufuegen,
    type AblagePlatzhalter,
    type NeuerPlatzhalter,
} from "./ablagePlatzhalter";

/** Zustand der sofort sichtbaren Ablage — siehe `ablagePlatzhalter.ts`. */
export function useAblagePlatzhalter() {
    const [eintraege, setEintraege] = useState<AblagePlatzhalter[]>([]);
    /** „Erneut versuchen“ je Platzhalter — Funktionen gehören nicht in den Zustand. */
    const nochmal = useRef(new Map<string, () => void>());

    const starte = useCallback((neue: NeuerPlatzhalter[]): string[] => {
        // Außerhalb des Updaters erzeugen: React ruft ihn erst beim Rendern auf,
        // die Ids braucht der Aufrufer aber sofort.
        const { liste: dazu, ids } = hinzufuegen([], neue);
        setEintraege((liste) => [...dazu, ...liste]);
        return ids;
    }, []);
    const fertig = useCallback((ids: string[]) => {
        ids.forEach((id) => nochmal.current.delete(id));
        setEintraege((liste) => alsFertig(liste, ids));
    }, []);
    const scheitern = useCallback((id: string, fehler: string, erneut?: () => void) => {
        if (erneut) nochmal.current.set(id, erneut);
        setEintraege((liste) => alsFehler(liste, id, fehler));
    }, []);
    const laeuftWieder = useCallback((id: string, text?: string) => {
        setEintraege((liste) => alsLaufend(liste, id, text));
    }, []);
    const weg = useCallback((ids: string[]) => {
        ids.forEach((id) => nochmal.current.delete(id));
        setEintraege((liste) => entfernen(liste, ids));
    }, []);
    const raeumen = useCallback(() => {
        setEintraege((liste) => (liste.some((p) => p.status === "fertig") ? fertigeRaeumen(liste) : liste));
    }, []);
    const erneutVersuchen = useCallback((id: string) => {
        const f = nochmal.current.get(id);
        if (f) f();
    }, []);
    const kannErneut = useCallback((id: string) => nochmal.current.has(id), []);

    return useMemo(
        () => ({ eintraege, starte, fertig, scheitern, laeuftWieder, weg, raeumen, erneutVersuchen, kannErneut }),
        [eintraege, starte, fertig, scheitern, laeuftWieder, weg, raeumen, erneutVersuchen, kannErneut],
    );
}

export type AblagePlatzhalterApi = ReturnType<typeof useAblagePlatzhalter>;
